import SafeEmailPreview from '../../common/SafeEmailPreview';
/**
 * Document Viewer Component
 * Displays the original email/document with zoom, search, and view mode controls
 */

import React, { useState, useCallback, useRef, useEffect } from 'react';
import { getApiBase, getAuthHeaders } from '../../../utils/api';

const AttachmentPreview = ({ attachment }) => {
  const [blobUrl, setBlobUrl] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    setLoading(true);
    setError(null);
    (async () => {
      try {
        const res = await fetch(`${getApiBase()}/files/${attachment.id}/view`, {
          headers: getAuthHeaders(),
          credentials: 'include',
        });
        if (!res.ok) {
          const text = await res.text().catch(() => res.statusText);
          throw new Error(`Failed to load attachment (${res.status}): ${text}`);
        }
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setBlobUrl(createdUrl);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Failed to load attachment');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [attachment.id]);

  const fileName = attachment.file_name || `Attachment ${attachment.id}`;
  const fileType = attachment.file_type || '';
  const isPdf = fileType.includes('pdf') || fileName.toLowerCase().endsWith('.pdf');
  const isImage = fileType.startsWith('image/') || /\.(png|jpe?g|gif|webp|bmp)$/i.test(fileName);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full text-gray-500 text-sm">
        Loading {fileName}...
      </div>
    );
  }
  if (error || !blobUrl) {
    return (
      <div className="flex items-center justify-center h-full text-red-600 text-sm">
        {error || 'Could not load attachment'}
      </div>
    );
  }
  if (isPdf) {
    return <iframe title={fileName} src={blobUrl} className="w-full h-full border-0" />;
  }
  if (isImage) {
    return (
      <div className="w-full h-full overflow-auto flex items-center justify-center bg-gray-100">
        <img src={blobUrl} alt={fileName} className="max-w-full max-h-full object-contain" />
      </div>
    );
  }
  return (
    <div className="flex flex-col items-center justify-center h-full text-gray-600 gap-2">
      <p className="text-sm">{fileName}</p>
      <a href={blobUrl} download={fileName} className="text-xs text-blue-600 hover:underline">
        Download
      </a>
    </div>
  );
};

const DocumentViewer = ({
  html,
  plainText,
  subject,
  sender,
  receivedAt,
  attachments = [],
  zoom = 100,
  onZoomChange,
  viewMode = 'html',
  onViewModeChange,
  searchQuery = '',
  onSearchChange,
  className = ''
}) => {
  // When the email body is empty but attachments exist, default the visible
  // tab to the first attachment so the reviewer always sees something useful.
  const initialTab = (!html && !plainText && attachments.length > 0)
    ? `attachment:${attachments[0].id}`
    : 'body';
  const [activeTab, setActiveTab] = useState(initialTab);

  useEffect(() => {
    // Reset to the appropriate tab when the underlying item changes (different
    // body/attachment set). Compares by attachment ID list to avoid resetting
    // mid-review when only zoom/view-mode props change.
    const next = (!html && !plainText && attachments.length > 0)
      ? `attachment:${attachments[0].id}`
      : 'body';
    setActiveTab(next);
  }, [html, plainText, (attachments || []).map(a => a.id).join('|')]);
  const [localZoom, setLocalZoom] = useState(zoom);
  const [localViewMode, setLocalViewMode] = useState(viewMode);
  const [localSearch, setLocalSearch] = useState(searchQuery);
  const [searchMatches, setSearchMatches] = useState(0);
  const [currentMatch, setCurrentMatch] = useState(0);
  const contentRef = useRef(null);
  const searchInputRef = useRef(null);

  // Sync with props
  useEffect(() => {
    setLocalZoom(zoom);
  }, [zoom]);

  useEffect(() => {
    setLocalViewMode(viewMode);
  }, [viewMode]);

  // Zoom handlers
  const handleZoomIn = useCallback(() => {
    const newZoom = Math.min(localZoom + 10, 200);
    setLocalZoom(newZoom);
    onZoomChange?.(newZoom);
  }, [localZoom, onZoomChange]);

  const handleZoomOut = useCallback(() => {
    const newZoom = Math.max(localZoom - 10, 50);
    setLocalZoom(newZoom);
    onZoomChange?.(newZoom);
  }, [localZoom, onZoomChange]);

  const handleZoomReset = useCallback(() => {
    setLocalZoom(100);
    onZoomChange?.(100);
  }, [onZoomChange]);

  // View mode handler
  const handleViewModeChange = useCallback((mode) => {
    setLocalViewMode(mode);
    onViewModeChange?.(mode);
  }, [onViewModeChange]);

  // Search handlers
  const handleSearch = useCallback((query) => {
    setLocalSearch(query);
    onSearchChange?.(query);

    if (!query || !contentRef.current) {
      setSearchMatches(0);
      setCurrentMatch(0);
      return;
    }

    // Use browser's find functionality
    if (window.find) {
      // Clear previous selection
      window.getSelection()?.removeAllRanges();

      // Count matches
      const content = contentRef.current.innerText || '';
      const matches = content.toLowerCase().split(query.toLowerCase()).length - 1;
      setSearchMatches(matches);
      setCurrentMatch(matches > 0 ? 1 : 0);

      // Highlight first match
      if (matches > 0) {
        window.find(query, false, false, true, false, true, false);
      }
    }
  }, [onSearchChange]);

  const handleNextMatch = useCallback(() => {
    if (searchMatches > 0 && window.find) {
      window.find(localSearch, false, false, true, false, true, false);
      setCurrentMatch(prev => (prev >= searchMatches ? 1 : prev + 1));
    }
  }, [localSearch, searchMatches]);

  const handlePrevMatch = useCallback(() => {
    if (searchMatches > 0 && window.find) {
      window.find(localSearch, false, true, true, false, true, false);
      setCurrentMatch(prev => (prev <= 1 ? searchMatches : prev - 1));
    }
  }, [localSearch, searchMatches]);

  // Focus search input on Ctrl+F
  const handleKeyDown = useCallback((e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'f') {
      e.preventDefault();
      searchInputRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);

  // Get content based on view mode
  const getContent = () => {
    switch (localViewMode) {
      case 'text':
        return plainText || html || '';
      case 'raw':
        return html || '';
      default:
        return plainText || html || '';
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleString();
    } catch {
      return dateStr;
    }
  };

  const showingBody = activeTab === 'body';
  const activeAttachment = !showingBody
    ? attachments.find(a => `attachment:${a.id}` === activeTab)
    : null;

  return (
    <div className={`flex flex-col h-full bg-white ${className}`}>
      {/* Tab strip — only shown when there are attachments to view alongside the body */}
      {attachments.length > 0 && (
        <div className="flex-shrink-0 border-b border-gray-200 bg-white px-3 py-1 flex items-center gap-1 overflow-x-auto">
          <button
            onClick={() => setActiveTab('body')}
            className={`px-3 py-1.5 text-xs font-medium rounded transition-colors whitespace-nowrap ${
              showingBody
                ? 'bg-blue-50 text-blue-700'
                : 'text-gray-600 hover:bg-gray-50'
            }`}
            title="Email body"
          >
            Email body{(!html && !plainText) ? ' (empty)' : ''}
          </button>
          {attachments.map(att => {
            const tabId = `attachment:${att.id}`;
            return (
              <button
                key={att.id}
                onClick={() => setActiveTab(tabId)}
                className={`px-3 py-1.5 text-xs font-medium rounded transition-colors whitespace-nowrap ${
                  activeTab === tabId
                    ? 'bg-blue-50 text-blue-700'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
                title={att.file_name}
              >
                {att.file_name || `Attachment ${att.id.slice(0, 6)}`}
              </button>
            );
          })}
        </div>
      )}

      {/* Toolbar — body-mode only; attachments render in their own preview pane */}
      {showingBody && (
      <div className="flex-shrink-0 border-b border-gray-200 bg-gray-50 px-3 py-2">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          {/* View mode toggle */}
          <div className="flex items-center bg-white rounded-md border border-gray-200 overflow-hidden">
            <button
              onClick={() => handleViewModeChange('html')}
              className={`px-3 py-1.5 text-xs font-medium transition-colors ${
                localViewMode === 'html'
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              title="Rendered HTML"
            >
              Email
            </button>
            <button
              onClick={() => handleViewModeChange('text')}
              className={`px-3 py-1.5 text-xs font-medium border-l border-gray-200 transition-colors ${
                localViewMode === 'text'
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              title="Plain Text"
            >
              Text
            </button>
            <button
              onClick={() => handleViewModeChange('raw')}
              className={`px-3 py-1.5 text-xs font-medium border-l border-gray-200 transition-colors ${
                localViewMode === 'raw'
                  ? 'bg-blue-50 text-blue-700'
                  : 'text-gray-600 hover:bg-gray-50'
              }`}
              title="Raw Source"
            >
              Raw
            </button>
          </div>

          {/* Zoom controls */}
          <div className="flex items-center gap-1">
            <button
              onClick={handleZoomOut}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
              title="Zoom out (-)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
              </svg>
            </button>
            <button
              onClick={handleZoomReset}
              className="px-2 py-1 text-xs text-gray-600 hover:bg-gray-100 rounded min-w-[48px]"
              title="Reset zoom"
            >
              {localZoom}%
            </button>
            <button
              onClick={handleZoomIn}
              className="p-1.5 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
              title="Zoom in (+)"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </button>
          </div>

          {/* Search */}
          <div className="flex items-center gap-1">
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                value={localSearch}
                onChange={(e) => handleSearch(e.target.value)}
                placeholder="Search..."
                className="w-32 sm:w-40 pl-7 pr-2 py-1.5 text-xs border border-gray-200 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
              <svg
                className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
            {searchMatches > 0 && (
              <>
                <span className="text-xs text-gray-500">
                  {currentMatch}/{searchMatches}
                </span>
                <button
                  onClick={handlePrevMatch}
                  className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                  title="Previous match"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
                <button
                  onClick={handleNextMatch}
                  className="p-1 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded"
                  title="Next match"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
              </>
            )}
          </div>
        </div>
      </div>
      )}

      {/* Email metadata */}
      {showingBody && (subject || sender) && (
        <div className="flex-shrink-0 border-b border-gray-200 px-4 py-2 bg-gray-50">
          {subject && (
            <div className="text-sm font-medium text-gray-900 truncate" title={subject}>
              {subject}
            </div>
          )}
          <div className="flex items-center gap-4 text-xs text-gray-500 mt-1">
            {sender && <span className="truncate">From: {sender}</span>}
            {receivedAt && <span>Received: {formatDate(receivedAt)}</span>}
          </div>
        </div>
      )}

      {/* Content */}
      {showingBody ? (
        <div
          ref={contentRef}
          className="flex-1 overflow-auto p-4"
          style={{ fontSize: `${localZoom}%` }}
        >
          {localViewMode === "html" ? <SafeEmailPreview html={html || plainText || ""} plain={!html} /> : <pre className="whitespace-pre-wrap break-words">{getContent()}</pre>}

          {!html && !plainText && (
            <div className="flex items-center justify-center h-full text-gray-400">
              <div className="text-center">
                <svg className="w-12 h-12 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p>No document content available</p>
                {attachments.length > 0 && (
                  <p className="text-xs mt-1">Switch to an attachment tab above to view the forwarded document.</p>
                )}
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="flex-1 overflow-hidden bg-gray-100">
          {activeAttachment ? (
            <AttachmentPreview attachment={activeAttachment} />
          ) : (
            <div className="flex items-center justify-center h-full text-gray-400">
              Attachment not found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DocumentViewer;
